import { useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type {
  AllocationPreviewView,
  BillingAccountDetailView,
  ChargeView,
  PaymentMethod,
  PaymentPostedView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { AllocationLines } from './AllocationLines';
import { ApiRequestError, apiRequest } from './api';
import {
  amountInputText,
  canAdjustCharge,
  draftLinesFromPreview,
  evaluateAllocationDraft,
  paymentMethodKeys,
  parseAmountInput,
  postablePaymentMethods,
  type AllocationDraftLine,
} from './billing';

export type ErrorKey = ApiRequestError['messageKey'];

export function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

export interface BillingFormProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}/billing`. */
  base: string;
  csrfToken: string;
  account: Pick<BillingAccountDetailView, 'id' | 'currency'>;
  /** Called after the server accepted the posting, so the account is reloaded. */
  onPosted: () => Promise<void>;
  onError: (key: ErrorKey | null) => void;
}

/**
 * Posts a manual charge. The idempotency key lives as long as the form content: a retry after a
 * dropped connection repeats the same key, and the key changes only once a posting succeeded.
 */
export function ChargeForm({
  locale,
  base,
  csrfToken,
  account,
  onPosted,
  onError,
}: BillingFormProps): React.JSX.Element {
  const [description, setDescription] = useState('');
  const [amountText, setAmountText] = useState('');
  const [dueOn, setDueOn] = useState(todayInUtc);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const amountMinor = parseAmountInput(amountText);
  const showAmountProblem = amountText.trim() !== '' && amountMinor === null;

  async function submit(): Promise<void> {
    if (amountMinor === null) return;
    onError(null);
    try {
      await apiRequest(`${base}/accounts/${account.id}/charges`, {
        method: 'POST',
        body: { description: description.trim(), amountMinor, dueOn },
        csrfToken,
        idempotencyKey,
      });
      setDescription('');
      setAmountText('');
      setIdempotencyKey(crypto.randomUUID());
      await onPosted();
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="charge-form-title" sx={{ mb: 3 }}>
      <Typography variant="subtitle1" component="h3" id="charge-form-title" gutterBottom>
        {translate(locale, 'billing.charges.post.title')}
      </Typography>
      <Stack
        component="form"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ flexWrap: 'wrap' }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <TextField
          required
          size="small"
          label={translate(locale, 'billing.charges.post.description')}
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 200 } }}
          sx={{ minWidth: 240, flexGrow: 1 }}
        />
        <TextField
          required
          size="small"
          label={`${translate(locale, 'billing.charges.post.amount')} (${account.currency})`}
          value={amountText}
          error={showAmountProblem}
          helperText={showAmountProblem ? translate(locale, 'billing.invalidAmount') : undefined}
          onChange={(event) => {
            setAmountText(event.target.value);
          }}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          required
          size="small"
          type="date"
          label={translate(locale, 'billing.charges.post.dueOn')}
          value={dueOn}
          onChange={(event) => {
            setDueOn(event.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={amountMinor === null || description.trim() === '' || dueOn === ''}
        >
          {translate(locale, 'billing.charges.post.submit')}
        </Button>
      </Stack>
    </Box>
  );
}

export interface PaymentFormProps extends BillingFormProps {
  charges: readonly ChargeView[];
}

/**
 * Posts a payment in two steps, as the specification requires: the server previews the
 * oldest-due-first allocation, finance may edit the amounts, and only then is the payment posted
 * with the confirmed allocation. Whatever is not allocated stays on the account as credit.
 */
export function PaymentForm({
  locale,
  base,
  csrfToken,
  account,
  charges,
  onPosted,
  onError,
}: PaymentFormProps): React.JSX.Element {
  const [amountText, setAmountText] = useState('');
  const [paidOn, setPaidOn] = useState(todayInUtc);
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [payerReference, setPayerReference] = useState('');
  const [draft, setDraft] = useState<AllocationDraftLine[] | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const amountMinor = parseAmountInput(amountText);
  const showAmountProblem = amountText.trim() !== '' && amountMinor === null;
  const openCharges = charges
    .filter((charge) => canAdjustCharge(charge))
    .sort((left, right) => left.dueOn.localeCompare(right.dueOn));
  const evaluation =
    amountMinor !== null && draft !== null ? evaluateAllocationDraft(amountMinor, draft) : null;

  /** Any change to the amount invalidates a preview made for the earlier one. */
  function changeAmount(text: string): void {
    setAmountText(text);
    setDraft(null);
  }

  async function preview(): Promise<void> {
    if (amountMinor === null) return;
    onError(null);
    try {
      const proposal = await apiRequest<AllocationPreviewView>(
        `${base}/accounts/${account.id}/allocation-preview?amountMinor=${amountMinor}`,
      );
      const proposed = draftLinesFromPreview(proposal);
      const proposedIds = new Set(proposed.map((line) => line.chargeId));
      // Every other open charge is offered with an empty amount, so finance can redirect money.
      const others = openCharges
        .filter((charge) => !proposedIds.has(charge.id))
        .map((charge) => ({ chargeId: charge.id, amountText: '' }));
      setDraft([...proposed, ...others]);
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  async function submit(): Promise<void> {
    if (amountMinor === null || evaluation?.problem !== null) return;
    onError(null);
    try {
      await apiRequest<PaymentPostedView>(`${base}/payments`, {
        method: 'POST',
        body: {
          accountId: account.id,
          amountMinor,
          currency: account.currency,
          paidOn,
          method,
          ...(payerReference.trim() === '' ? {} : { payerReference: payerReference.trim() }),
          reference: reference.trim(),
          allocations: evaluation.lines,
        },
        csrfToken,
        idempotencyKey,
      });
      setAmountText('');
      setReference('');
      setPayerReference('');
      setDraft(null);
      setIdempotencyKey(crypto.randomUUID());
      await onPosted();
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="payment-form-title" sx={{ mb: 3 }}>
      <Typography variant="subtitle1" component="h3" id="payment-form-title" gutterBottom>
        {translate(locale, 'billing.payment.post.title')}
      </Typography>
      <Stack
        component="form"
        spacing={2}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField
            required
            size="small"
            label={`${translate(locale, 'billing.payment.amount')} (${account.currency})`}
            value={amountText}
            error={showAmountProblem}
            helperText={showAmountProblem ? translate(locale, 'billing.invalidAmount') : undefined}
            onChange={(event) => {
              changeAmount(event.target.value);
            }}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'billing.payment.paidOn')}
            value={paidOn}
            onChange={(event) => {
              setPaidOn(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'billing.payment.method')}
            value={method}
            onChange={(event) => {
              setMethod(event.target.value as PaymentMethod);
            }}
            sx={{ minWidth: 180 }}
          >
            {postablePaymentMethods.map((option) => (
              <MenuItem key={option} value={option}>
                {translate(locale, paymentMethodKeys[option])}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField
            required
            size="small"
            label={translate(locale, 'billing.payment.reference')}
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            sx={{ minWidth: 240, flexGrow: 1 }}
          />
          <TextField
            size="small"
            label={translate(locale, 'billing.payment.payerReference')}
            value={payerReference}
            onChange={(event) => {
              setPayerReference(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            sx={{ minWidth: 240, flexGrow: 1 }}
          />
        </Stack>
        <Box>
          <Button
            variant="outlined"
            disabled={amountMinor === null}
            onClick={() => {
              void preview();
            }}
          >
            {translate(locale, 'billing.payment.preview')}
          </Button>
        </Box>
        {draft !== null && amountMinor !== null && evaluation !== null && (
          <AllocationLines
            locale={locale}
            currency={account.currency}
            charges={charges}
            draft={draft}
            evaluation={evaluation}
            titleId="allocation-title"
            onChange={setDraft}
          />
        )}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={
              amountMinor === null || reference.trim() === '' || evaluation?.problem !== null
            }
          >
            {translate(locale, 'billing.payment.submit')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export type LedgerAction = 'creditNote' | 'reverseCharge' | 'reversePayment' | 'refund';

const actionSubmitKeys = {
  creditNote: 'billing.creditNote.submit',
  reverseCharge: 'billing.reverse.submit',
  reversePayment: 'billing.reverse.submit',
  refund: 'billing.refund.submit',
} as const;

const actionAmountKeys = {
  creditNote: 'billing.creditNote.amount',
  refund: 'billing.refund.amount',
} as const;

export interface LedgerActionFormProps {
  locale: SupportedLocale;
  action: LedgerAction;
  /** The route that takes the correction. */
  path: string;
  currency: string;
  /** A refund or credit note starts from the largest amount that is allowed. */
  suggestedAmountMinor?: string | undefined;
  csrfToken: string;
  onDone: () => Promise<void>;
  onCancel: () => void;
  onError: (key: ErrorKey | null) => void;
}

/**
 * A correction to one posted entry: a reason is always required, and credit notes and refunds also
 * take an amount. The form is mounted per attempt, so its idempotency key is stable across retries.
 */
export function LedgerActionForm({
  locale,
  action,
  path,
  currency,
  suggestedAmountMinor,
  csrfToken,
  onDone,
  onCancel,
  onError,
}: LedgerActionFormProps): React.JSX.Element {
  const takesAmount = action === 'creditNote' || action === 'refund';
  const [reason, setReason] = useState('');
  const [amountText, setAmountText] = useState(
    suggestedAmountMinor === undefined ? '' : amountInputText(suggestedAmountMinor),
  );
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const amountMinor = parseAmountInput(amountText);
  const amountValid = !takesAmount || amountMinor !== null;

  async function submit(): Promise<void> {
    if (!amountValid) return;
    onError(null);
    try {
      await apiRequest(path, {
        method: 'POST',
        body: takesAmount ? { amountMinor, reason: reason.trim() } : { reason: reason.trim() },
        csrfToken,
        idempotencyKey,
      });
      await onDone();
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  return (
    <Stack
      component="form"
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ py: 1, flexWrap: 'wrap' }}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {takesAmount && (
        <TextField
          required
          size="small"
          label={`${translate(locale, actionAmountKeys[action])} (${currency})`}
          value={amountText}
          error={amountText.trim() !== '' && amountMinor === null}
          onChange={(event) => {
            setAmountText(event.target.value);
          }}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ minWidth: 160 }}
        />
      )}
      <TextField
        required
        size="small"
        label={translate(locale, 'billing.reason')}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 500 } }}
        sx={{ minWidth: 240, flexGrow: 1 }}
      />
      <Button
        type="submit"
        size="small"
        variant="contained"
        color={action === 'creditNote' || action === 'refund' ? 'primary' : 'error'}
        disabled={reason.trim() === '' || !amountValid}
      >
        {translate(locale, actionSubmitKeys[action])}
      </Button>
      <Button size="small" onClick={onCancel}>
        {translate(locale, 'billing.cancel')}
      </Button>
    </Stack>
  );
}
